import styled from '@emotion/styled';
import { Button, Snackbar as _Snackbar } from '@material-ui/core';
import { SnackbarOrigin } from '@material-ui/core/Snackbar';
import { SyntheticEvent, FC } from 'react';
import { useReactiveVar } from '@apollo/client';

import { errorsVar } from '../../../cache';
import { AvailableError, SnackError } from '../../../operations/commands/Errors';
import globalState from '../../../operations/commands';

type Props = {
  anchorOrigin?: SnackbarOrigin;
  autoHideDuration?: number;
  action?: string;
  severity?: 'default' | 'error' | 'warning' | 'information' | 'success' | 'init';
  onClickAction?: (e: SyntheticEvent<HTMLElement>) => void;
  onRequestClear?: (e: SyntheticEvent<HTMLElement>) => void;
};

const ReactiveSnackbar: FC<Props> = ({
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  autoHideDuration = 8000,
  action = 'OK',
  severity = 'default' as const,
  onClickAction = (_) => globalState.disposeErrors(),
  onRequestClear = (_) => globalState.disposeErrors(),
}) => {
  const errors = useReactiveVar(errorsVar);
  const snackErrors = errors?.filter((error: AvailableError): error is SnackError => {
    //return ['NonFatalError', 'UnimportantError'].includes(error.name);
    return error instanceof SnackError;
  });

  if (!snackErrors?.length) return null;

  return (
    <div>
      {snackErrors.map((error, i) => (
        <Snackbar
          key={i}
          severity={error.severity ?? severity}
          anchorOrigin={error?.anchorOrigin ?? anchorOrigin}
          autoHideDuration={error.autoHideDuration ?? autoHideDuration}
          open={!!error}
          message={
            error.message && <span>{error.context ? `[${error.context}]: ${error.message}` : error.message}</span>
          }
          action={
            action && (
              <Button color="inherit" size="small" onClick={onClickAction}>
                <Message>{action}</Message>
              </Button>
            )
          }
          onClose={onRequestClear}
        />
      ))}
    </div>
  );
};

const Message = styled('span')({ wordBreak: 'break-all', color: 'inherit' });

const Snackbar = styled(_Snackbar)<{ severity: 'default' | 'error' | 'warning' | 'information' | 'success' | 'init' }>(
  (props) => {
    return {
      zIndex: 2147483000 + 1, // 2147483000 is intercomIcon
      '& > .MuiSnackbarContent-root': {
        // snackbarを上書きする際、一瞬initial stateで定義したseverityのbackground-colorが表示される為initのenum stringを追加して背景をunsetしている
        ...(props.severity === 'init' && {
          backgroundColor: 'unset',
        }),
        ...(props.severity === 'error' && {
          backgroundColor: '#FE7877',
        }),
        ...(props.severity === 'warning' && {
          backgroundColor: '#FCB100',
        }),
        ...(props.severity === 'information' && {
          backgroundColor: '#5FA9FF',
        }),
        ...(props.severity === 'success' && {
          backgroundColor: '#2DCC70',
        }),
      },
    };
  },
);

export default ReactiveSnackbar;
